import { createContext, useContext, useState, ReactNode } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createAppTheme } from '../theme/theme';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  themeMode: 'light' | 'dark';
  screenSize: {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
  };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1024px)');
  const isDesktop = useMediaQuery('(min-width: 1025px)');

  const [themeMode, setThemeMode] = useState<'light' | 'dark'>('light');
  const [theme, setTheme] = useState(() => createAppTheme('light'));

  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
    setTheme(createAppTheme(newMode));
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      toggleTheme,
      themeMode,
      screenSize: {
        isMobile,
        isTablet,
        isDesktop,
      },
    }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within an ThemeProvider');
  }
  return context;
};